import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Grid from '../components/Grid';
import Pagination from '../components/Pagination';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { Snackbar, Alert } from '@mui/material';
import { Person as PersonIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { API_BASE_URL,AUTHORIZED_ROLES } from '../constants/property';


const Attendance = () => {
  const location = useLocation();
  const navigate = useNavigate(); 
  const {hasAnyRole } = useAuth();
  const isAdministrator  = hasAnyRole(AUTHORIZED_ROLES);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [attendanceData, setAttendanceData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [sortColumn, setSortColumn] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const defaultVisibleColumns = {
    employeeId: true,
    date: true,
    name: true,
    startTime: true,
    startLocation: true,
    startImage: true,
    endTime: true,
    endLocation: true,
    endImage: true,
    status: true,
    advance: true,
    role: true,
    actions: true,
    // hidden by default
    image: false,
    phone: false,
    email: false,
    address: false,
    checkIn: false,
    checkOut: false,
    location: false
  };
  const [visibleColumns, setVisibleColumns] = useState(defaultVisibleColumns);
  // Helper function to get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  /** API expects date-only string: YYYY-MM-DD (e.g. 2026-04-08). */
  const ATTENDANCE_DATE_YMD = /^\d{4}-\d{2}-\d{2}$/;

  const isValidYmdParts = (year, month, day) => {
    const y = Number(year);
    const m = Number(month);
    const d = Number(day);
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return false;
    const dt = new Date(y, m - 1, d);
    return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
  };

  const toAttendanceDateYmd = (value) => {
    if (value == null || value === '') return '';
    if (typeof value === 'number' && Number.isFinite(value)) {
      const dt = new Date(value);
      if (Number.isNaN(dt.getTime())) return '';
      const y = dt.getFullYear();
      const mo = String(dt.getMonth() + 1).padStart(2, '0');
      const d = String(dt.getDate()).padStart(2, '0');
      return `${y}-${mo}-${d}`;
    }
    if (Array.isArray(value) && value.length >= 3) {
      const y = value[0];
      const mo = value[1];
      const d = value[2];
      const yNum = Number(y);
      const mNum = Number(mo);
      const dNum = Number(d);
      if (isValidYmdParts(yNum, mNum, dNum)) {
        return `${yNum}-${String(mNum).padStart(2, '0')}-${String(dNum).padStart(2, '0')}`;
      }
      return '';
    }
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const yRaw = value.year ?? value.y;
      const mRaw = value.monthValue ?? value.month ?? value.mo;
      const dRaw = value.dayOfMonth ?? value.day ?? value.d;
      if (yRaw != null && mRaw != null && dRaw != null) {
        const yNum = Number(yRaw);
        const mNum = Number(mRaw);
        const dNum = Number(dRaw);
        if (isValidYmdParts(yNum, mNum, dNum)) {
          return `${yNum}-${String(mNum).padStart(2, '0')}-${String(dNum).padStart(2, '0')}`;
        }
      }
    }
    let s = String(value).trim();
    if (!s) return '';
    if (s.includes('T')) {
      s = s.split('T')[0].trim();
    }
    if (ATTENDANCE_DATE_YMD.test(s)) {
      const [y, mo, d] = s.split('-');
      return isValidYmdParts(y, mo, d) ? `${y}-${mo}-${d}` : '';
    }
    const ddmmyyyy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (ddmmyyyy) {
      const day = ddmmyyyy[1].padStart(2, '0');
      const month = ddmmyyyy[2].padStart(2, '0');
      const year = ddmmyyyy[3];
      return isValidYmdParts(year, month, day) ? `${year}-${month}-${day}` : '';
    }
    const dateParts = s.match(/(\w+)\s+(\d+),\s+(\d{4})/);
    if (dateParts) {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthIndex = months.indexOf(dateParts[1]);
      if (monthIndex !== -1) {
        const y = dateParts[3];
        const mo = String(monthIndex + 1).padStart(2, '0');
        const d = String(dateParts[2]).padStart(2, '0');
        return isValidYmdParts(y, mo, d) ? `${y}-${mo}-${d}` : '';
      }
    }
    const dt = new Date(s);
    if (!Number.isNaN(dt.getTime())) {
      const y = dt.getFullYear();
      const mo = String(dt.getMonth() + 1).padStart(2, '0');
      const d = String(dt.getDate()).padStart(2, '0');
      return `${y}-${mo}-${d}`;
    }
    return '';
  };

  const [formData, setFormData] = useState({
    employeeId: '',
    firstName: '',
    lastName: '',
    empName: '',
    role: '',
    phone: '',
    email: '',
    attendanceDate: getTodayDate(),
    startTime: '',
    startLocation: '',
    endTime: '',
    endLocation: '',
    status: 'PRESENT',
    advance: '',
    comments: ''
  });

  // Filter states
  const [showFilters, setShowFilters] = useState(true);
  const [datePreset, setDatePreset] = useState('');
  const [dateDisplay, setDateDisplay] = useState('');
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [showCustomCalendar, setShowCustomCalendar] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedName, setSelectedName] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [nameInputValue, setNameInputValue] = useState('');
  const [showNameDropdown, setShowNameDropdown] = useState(false);
  const [statusInputValue, setStatusInputValue] = useState('');
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isEmployeeModalOpen, setIsEmployeeModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
  const [previewImageSrc, setPreviewImageSrc] = useState('');
  const [editFormData, setEditFormData] = useState({
    name: '',
    phoneNumber: '',
    alternatePhone: '',
    address: '',
    email: '',
    role: '',
    site: '',
    dateOfJoin: '',
    employeeImage: null
  });

  // Check if modal should be opened from navigation
  useEffect(() => {
    if (location.state?.openModal) {
      setIsModalOpen(true);
      // Clear the state to prevent reopening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleEmployeeClick = (record) => {
    // Map record to employee format
    const employee = {
      id: record.employeeId || '',
      employeeId: record.employeeId || '',
      name: record.name || '',
      role: record.role || '',
      phoneNumber: record.phoneNumber || '',
      alternatePhone: record.alternatePhone || '',
      email: record.email || '',
      address: record.address || '',
      site: record.site || '',
      dateOfJoin: record.dateOfJoin || '',
      employeeImage: record.employeeImage || null
    };
    setSelectedEmployee(employee);
    setEditFormData(employee);
    setIsEmployeeModalOpen(true);
  };

  const handleCloseEmployeeModal = () => {
    setIsEmployeeModalOpen(false);
    setSelectedEmployee(null);
  };

  const handleOpenImagePreview = (imageSrc) => {
    if (!imageSrc) return;
    setPreviewImageSrc(imageSrc);
    setIsImagePreviewOpen(true);
  };

  const handleCloseImagePreview = () => {
    setIsImagePreviewOpen(false);
    setPreviewImageSrc('');
  };

  const handleEditProfile = () => {
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    // Reset form data to original employee data
    setEditFormData(selectedEmployee);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setEditFormData(prev => ({
          ...prev,
          employeeImage: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveProfile = () => {
    // Update employee data
    setSelectedEmployee(editFormData);
    setIsEditModalOpen(false);
    console.log('Profile updated:', editFormData);
  };

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isEmployeeModalOpen) {
        handleCloseEmployeeModal();
      }
    };
    if (isEmployeeModalOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isEmployeeModalOpen]);

  // Format time from time object or 24h string to 12h format
  const formatTime = (timeData) => {
    if (!timeData) return '-';
    
    let hour, minute;
    
    // Handle time object format {hour, minute, second, nano}
    if (typeof timeData === 'object' && timeData.hour !== undefined) {
      hour = timeData.hour || 0;
      minute = timeData.minute || 0;
    } 
    // Handle string format (HH:MM or HH:MM:SS)
    else if (typeof timeData === 'string') {
      const parts = timeData.split(':');
      hour = parseInt(parts[0], 10) || 0;
      minute = parseInt(parts[1], 10) || 0;
    } else {
      return '-';
    }
    
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${String(minute).padStart(2, '0')} ${ampm}`;
  };

  /** Display helper: normalize any API date to YYYY-MM-DD (no UTC shift for ISO dates). */
  const formatAttendanceDate = (dateInput) => {
    const ymd = toAttendanceDateYmd(dateInput);
    return ymd || '-';
  };

  const toImageSrc = (rawImageData) => {
    if (!rawImageData) return null;
    const v = String(rawImageData).trim();
    if (!v) return null;
    // If backend already returns a data URL, keep it.
    if (v.startsWith('data:image/')) return v;
    // Default to PNG for plain base64 strings.
    return `data:image/png;base64,${v}`;
  };

  /** Match backend field names (POST uses empId; GET may return empId / empID / employeeId). */
  const employeeIdFromApiItem = (item) => {
    const raw =
      item?.employeeId ??
      item?.empId ??
      item?.empID ??
      item?.employee_id;
    if (raw == null) return '';
    const s = String(raw).trim();
    return s;
  };

  // Fetch attendance data from API
  const fetchAttendanceData = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/attendance`, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Attendance API Response:', data);
      
      // Transform API data to match table structure
      const transformedData = data.map(item => {
        const employeeId = employeeIdFromApiItem(item);
        // Record ID is the attendance record ID (for update/delete operations)
        const recordId = item.id;
        
        return {
          employeeId: employeeId,
          recordId: recordId, // Keep record ID for API operations (update/delete)
          date: formatAttendanceDate(item.attendanceDate ?? item.date),
          name: item.empName || `${item.firstName || ''} ${item.lastName || ''}`.trim(),
          phoneNumber: item.phone || '-',
          email: item.email || '-',
          role: item.role || '-',
          address: '-', // API doesn't provide this
          alternateNo: '-', // API doesn't provide this
          advance: item.advance ? item.advance.toString() : '-',
          timeIn: formatTime(item.timeIn),
          timeOut: formatTime(item.timeOut),
          status: item.status === 'PRESENT' ? 'Present' : item.status === 'ABSENT' ? 'Absent' : item.status || '-',
          comments: item.comments || '-',
          location:
            item.location != null && item.location !== ''
              ? String(item.location)
              : '-',
          endLocation:
            item.endLocation != null && item.endLocation !== ''
              ? String(item.endLocation)
              : '-',
          employeeImage: toImageSrc(item.imageData || item.employeeImage || item.photoBase64 || item.photo),
          startImage: toImageSrc(item.startImage),
          endImage: toImageSrc(item.endImage),
          // Keep original data for reference
          originalData: item
        };
      });
      
      setAttendanceData(transformedData);
      
    } catch (err) {
      console.error('Error fetching attendance data:', err);
      setError(err.message || 'Failed to fetch attendance data');
      setAttendanceData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Load attendance data on component mount
  useEffect(() => {
    fetchAttendanceData();
  }, []);

  // API Functions for CRUD operations
  const createAttendanceRecord = async (recordData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/attendance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(recordData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Create attendance response:', data);
      return data;
      
    } catch (err) {
      console.error('Error creating attendance record:', err);
      throw err;
    }
  };

  const updateAttendanceRecord = async (id, recordData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/attendance/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(recordData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Update attendance response:', data);
      return data;
      
    } catch (err) {
      console.error('Error updating attendance record:', err);
      throw err;
    }
  };

  const deleteAttendanceRecord = async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/attendance/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      console.log('Delete attendance response:', response);
      return true;
      
    } catch (err) {
      console.error('Error deleting attendance record:', err);
      throw err;
    }
  };

  // Static fallback data (removed - now using API)
  const staticAttendanceData = [
    {
      id: "TK001",
      date: "Oct 09, 2025",
      name: "Selva",
      phoneNumber: "8754697585",
      email: "selva@gmail.com",
      role: "Supervisor",
      address: "Akshya Nagar ",
      alternateNo: "8574697842",
      advance: "-",
      checkIn: "09:00 AM",
      checkOut: "07:00 PM",
      status: "Present",
    },
    {
      id: "TK002",
      date: "Oct 08, 2025",
      name: "Emma",
      phoneNumber: "8754697585",
      email: "emma@gmail.com",
      role: "Staff",
      address: "127 North Stt",
      alternateNo: "8574697842",
      advance: "-",
      checkIn: "09:00 AM",
      checkOut: "07:00 PM",
      status: "Present",
    },
    {
      id: "TK003",
      date: "Oct 07, 2025",
      name: "Mark",
      phoneNumber: "8754697585",
      email: "mark@gmail.com",
      role: "Manager",
      address: "520 Ashe Ave",
      alternateNo: "8574697842",
      advance: "-",
      checkIn: "-",
      checkOut: "-",
      status: "Absent",
    },
    {
      id: "TK004",
      date: "Oct 06, 2025",
      name: "John",
      phoneNumber: "8754697585",
      email: "john@gmail.com",
      role: "Staff",
      address: "505 Martin Luther",
      alternateNo: "8574697842",
      advance: "-",
      checkIn: "-",
      checkOut: "-",
      status: "Absent",
    },
    {
      id: "TK005",
      date: "Oct 06, 2025",
      name: "Lily",
      phoneNumber: "8754697585",
      email: "lily@gmail.com",
      role: "SuperVisor",
      address: "2116-D New Bern",
      alternateNo: "8574697842",
      advance: "-",
      checkIn: "-",
      checkOut: "-",
      status: "Absent",
    },  
    {
      id: "TK006",
      date: "Oct 01, 2025",
      name: "Daniel",
      phoneNumber: "8754697585",
      email: "daniel@gmail.com",
      role: "Staff",
      address: "400 Donald Ross Dr",
      alternateNo: "8574697842",
      advance: "-",
      checkIn: "09:00 AM",
      checkOut: "07:00 PM",
      status: "Present",
    },
    {
      id: "TK007",
      date: "Oct 01, 2025",
      name: "Daniel",
      phoneNumber: "8754697585",
      email: "daniel@gmail.com",
      role: "Manager",
      address: "400 Donald Ross Dr",
      alternateNo: "8574697842",
      advance: "-",
      checkIn: "09:00 AM",
      checkOut: "07:00 PM",
      status: "Present",
    },
    {
      id: "TK008",
      date: "Oct 01, 2025",
      name: "Jude",
      phoneNumber: "7548796575",
      email: "jude@gmail.com",
      role: "Staff",
      address: "3000 New Bern Ave",
      alternateNo: "7452145527",
      advance: "7000",
      checkIn: "-",
      checkOut: "-",
      status: "Absent",
    },
    {
      id: "TK009",
      date: "Oct 01, 2025",
      name: "Robison",
      phoneNumber: "8754697585",
      email: "robison@gmail.com",
      role: "Manger",
      address: "Raleigh, NC",
      alternateNo: "5748965587",
      advance: "9000",
      checkIn: "09:00 AM",
      checkOut: "07:00 PM",
      status: "Present",
    },
    {
      id: "TK0010",
      date: "Oct 01, 2025",
      name: "Peter",
      phoneNumber: "8754697585",
      email: "peter8745@gmail.com",
      role: "Supervisor",
      address: "1070 Partners Way",
      alternateNo: "7548796575",
      advance: "10000",
      checkIn: "09:00 AM",
      checkOut: "07:00 PM",
      status: "Present",
    },
  ];

  // Filter options (using attendanceData from API)
  const nameOptions = useMemo(() => {
    const uniqueNames = [...new Set(attendanceData.map(item => item.name))];
    return [{ value: 'all', label: 'All' }, ...uniqueNames.map(name => ({ value: name, label: name }))];
  }, [attendanceData]);

  const statusOptions = [
    { value: 'all', label: 'All' },
    { value: 'Present', label: 'Present' },
    { value: 'Absent', label: 'Absent' }
  ];

  // Date preset display and handling
  useEffect(() => {
    if (!datePreset) {
      setDateDisplay('');
      return;
    }
    
    const now = new Date();
    // Same "Mar 16, 2026" style as userDetails.js
    const toDisplay = (d) => d.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
    const clone = (d) => new Date(d.getTime());
    let start = clone(now);
    let end = clone(now);
    
    switch (datePreset) {
      case 'today':
        break;
      case 'yesterday':
        start.setDate(start.getDate() - 1); 
        end.setDate(end.getDate() - 1); 
        break;
      case 'last7':
        start.setDate(start.getDate() - 6); 
        break;
      case 'last30':
        start.setDate(start.getDate() - 29); 
        break;
      case 'thisMonth':
        start = new Date(now.getFullYear(), now.getMonth(), 1); 
        break;
      case 'lastMonth': {
        const firstLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
        start = firstLastMonth; 
        end = lastLastMonth; 
        break;
      }
      case 'custom':
        if (!dateDisplay) {
          setDateDisplay(`${toDisplay(start)} - ${toDisplay(end)}`);
        }
        return;
      default:
        break;
    }
    setDateDisplay(`${toDisplay(start)} - ${toDisplay(end)}`);
  }, [datePreset]);

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatDate = (date) => {
    if (!date) return '';
    // Match userDetails.js ("Mar 16, 2026")
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
  };

  const isSameDate = (date1, date2) => {
    if (!date1 || !date2) return false;
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  };

  const isDateInRange = (date, startDate, endDate) => {
    if (!startDate || !endDate || !date) return false;
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const s = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const e = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    return d >= s && d <= e;
  };

  const handleDatePresetChange = (preset) => {
    setDatePreset(preset);
    setShowDateDropdown(false);
    setCurrentPage(1);
    if (preset === 'custom') {
      setShowCustomCalendar(true);
    } else {
      setShowCustomCalendar(false);
    }
  };

  const handleDateClick = (date) => {
    if (!customStartDate || (customStartDate && customEndDate)) {
      setCustomStartDate(date);
      setCustomEndDate(null);
    } else if (customStartDate && !customEndDate) {
      if (date < customStartDate) {
        setCustomEndDate(customStartDate);
        setCustomStartDate(date);
      } else {
        setCustomEndDate(date);
      }
    }
  };

  const handleApplyCustomRange = () => {
    if (customStartDate && customEndDate) {
      setDateDisplay(`${formatDate(customStartDate)} - ${formatDate(customEndDate)}`);
      setShowCustomCalendar(false);
      setShowDateDropdown(false);
      setCurrentPage(1);
    }
  };

  const handleCancelCustomRange = () => {
    setShowCustomCalendar(false);
    setShowDateDropdown(false);
    setCustomStartDate(null);
    setCustomEndDate(null);
  };

  const handleClearDate = (e) => {
    e.stopPropagation();
    setDatePreset('');
    setDateDisplay('');
    setCustomStartDate(null);
    setCustomEndDate(null);
    setShowCustomCalendar(false);
    setShowDateDropdown(false);
  };

  const handleClearName = (e) => {
    e.stopPropagation();
    setSelectedName('all');
    setNameInputValue('');
    setShowNameDropdown(false);
    setCurrentPage(1);
  };

  const handleClearStatus = (e) => {
    e.stopPropagation();
    setSelectedStatus('all');
    setStatusInputValue('');
    setShowStatusDropdown(false);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setDatePreset('');
    setDateDisplay('');
    setCustomStartDate(null);
    setCustomEndDate(null);
    setSelectedName('all');
    setSelectedStatus('all');
    setNameInputValue('');
    setStatusInputValue('');
    setCurrentPage(1);
    setShowCustomCalendar(false);
    setShowDateDropdown(false);
    setShowNameDropdown(false);
    setShowStatusDropdown(false);
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  // Sync input value with selected name when selectedName changes externally (like clear filters)
  const isTypingRef = useRef(false);
  
  useEffect(() => {
    if (!isTypingRef.current) {
      if (selectedName === 'all') {
        setNameInputValue('');
      } else {
        const selectedOption = nameOptions.find(opt => opt.value === selectedName);
        if (selectedOption) {
          setNameInputValue(selectedOption.label);
        } else {
          setNameInputValue('');
        }
      }
    }
  }, [selectedName, nameOptions]);

  // Filtered name options based on input
  const filteredNameOptions = useMemo(() => {
    if (!nameInputValue.trim()) {
      return nameOptions;
    }
    const searchTerm = nameInputValue.toLowerCase();
    return nameOptions.filter(opt => 
      opt.label.toLowerCase().includes(searchTerm)
    );
  }, [nameOptions, nameInputValue]);

  const handleNameInputChange = (e) => {
    const value = e.target.value;
    isTypingRef.current = true;
    setNameInputValue(value);
    setShowNameDropdown(true);
    
    // Reset to 'all' if input is cleared
    if (!value.trim()) {
      setSelectedName('all');
    }
    
    // Reset typing flag after a short delay
    setTimeout(() => {
      isTypingRef.current = false;
    }, 100);
  };

  const handleNameSelect = (option) => {
    isTypingRef.current = false;
    setSelectedName(option.value);
    setNameInputValue(option.label);
    setShowNameDropdown(false);
    setCurrentPage(1);
  };

  // Status autocomplete logic
  const isTypingStatusRef = useRef(false);
  
  useEffect(() => {
    if (!isTypingStatusRef.current) {
      if (selectedStatus === 'all') {
        setStatusInputValue('');
      } else {
        const selectedOption = statusOptions.find(opt => opt.value === selectedStatus);
        if (selectedOption) {
          setStatusInputValue(selectedOption.label);
        } else {
          setStatusInputValue('');
        }
      }
    }
  }, [selectedStatus, statusOptions]);

  // Filtered status options based on input
  const filteredStatusOptions = useMemo(() => {
    if (!statusInputValue.trim()) {
      return statusOptions;
    }
    const searchTerm = statusInputValue.toLowerCase();
    return statusOptions.filter(opt => 
      opt.label.toLowerCase().includes(searchTerm)
    );
  }, [statusOptions, statusInputValue]);

  const handleStatusInputChange = (e) => {
    const value = e.target.value;
    isTypingStatusRef.current = true;
    setStatusInputValue(value);
    setShowStatusDropdown(true);
    
    // Reset to 'all' if input is cleared
    if (!value.trim()) {
      setSelectedStatus('all');
    }
    
    // Reset typing flag after a short delay
    setTimeout(() => {
      isTypingStatusRef.current = false;
    }, 100);
  };

  const handleStatusSelect = (option) => {
    isTypingStatusRef.current = false;
    setSelectedStatus(option.value);
    setStatusInputValue(option.label);
    setShowStatusDropdown(false);
    setCurrentPage(1);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDateDropdown && !event.target.closest('.date-dropdown-container')) {
        setShowDateDropdown(false);
      }
      if (showNameDropdown && !event.target.closest('.name-autocomplete-container')) {
        setShowNameDropdown(false);
      }
      if (showStatusDropdown && !event.target.closest('.status-autocomplete-container')) {
        setShowStatusDropdown(false);
      }
      if (showColumnMenu && !event.target.closest('.column-menu-container')) {
        setShowColumnMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDateDropdown, showNameDropdown, showStatusDropdown, showColumnMenu]);

  // Check if any filters are applied
  const hasActiveFilters = useMemo(() => {
    return datePreset !== '' || dateDisplay !== '' || selectedName !== 'all' || selectedStatus !== 'all';
  }, [datePreset, dateDisplay, selectedName, selectedStatus]);

  // Same parsing/display as userDetails.js (date of joining column)
  const parseDateValue = (dateValue) => {
    if (!dateValue) return new Date(0);

    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      const [year, month, day] = dateValue.split('-').map(Number);
      return new Date(year, month - 1, day);
    }

    const parsed = new Date(dateValue);
    return Number.isNaN(parsed.getTime())
      ? new Date(0)
      : new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  };

  const formatDateMMMDDYYYY = (dateValue) => {
    if (!dateValue || dateValue === '-') return '-';
    const parsed = parseDateValue(dateValue);
    if (parsed.getTime() === 0) return '-';
    return parsed.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
  };

  // Inclusive calendar-day bounds for the active date preset (mirrors date preset useEffect)
  const filterDateBounds = useMemo(() => {
    if (!datePreset) return null;

    const now = new Date();
    const clone = (d) => new Date(d.getTime());
    let start;
    let end;

    switch (datePreset) {
      case 'today':
        start = clone(now);
        end = clone(now);
        break;
      case 'yesterday':
        start = clone(now);
        start.setDate(start.getDate() - 1);
        end = clone(start);
        break;
      case 'last7':
        end = clone(now);
        start = clone(now);
        start.setDate(start.getDate() - 6);
        break;
      case 'last30':
        end = clone(now);
        start = clone(now);
        start.setDate(start.getDate() - 29);
        break;
      case 'thisMonth':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = clone(now);
        break;
      case 'lastMonth':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          start = customStartDate;
          end = customEndDate;
        } else if (dateDisplay && dateDisplay.includes(' - ')) {
          const parts = dateDisplay.split(' - ');
          const pa = parseDateValue(parts[0].trim());
          const pb = parseDateValue(parts[parts.length - 1].trim());
          if (!pa.getTime() || !pb.getTime()) return null;
          start = pa;
          end = pb;
        } else {
          return null;
        }
        break;
      default:
        return null;
    }

    return {
      start: new Date(start.getFullYear(), start.getMonth(), start.getDate()),
      end: new Date(end.getFullYear(), end.getMonth(), end.getDate()),
    };
  }, [datePreset, customStartDate, customEndDate, dateDisplay]);

  // Compute filtered and sorted data
  const filteredData = useMemo(() => {
    let filtered = attendanceData.filter(item => {
      const nameOk = selectedName === 'all' || (item.name || '').toString().toLowerCase() === selectedName.toString().toLowerCase();
      const statusOk = selectedStatus === 'all' || (item.status || '').toString().toLowerCase() === selectedStatus.toString().toLowerCase();
      let dateOk = true;
      if (filterDateBounds) {
        const d = parseDateValue(item.date === '-' ? '' : item.date);
        if (!d.getTime()) {
          dateOk = false;
        } else {
          const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
          dateOk = day >= filterDateBounds.start && day <= filterDateBounds.end;
        }
      }
      return nameOk && statusOk && dateOk;
    });

    // Apply sorting
    if (sortColumn) {
      filtered = [...filtered].sort((a, b) => {
        let aValue = a[sortColumn];
        let bValue = b[sortColumn];

        // Handle different data types
        if (sortColumn === 'date') {
          aValue = parseDateValue(aValue === '-' ? '' : aValue);
          bValue = parseDateValue(bValue === '-' ? '' : bValue);
        } else if (sortColumn === 'advance') {
          // Handle numeric values
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
        } else {
          // Handle string values
          aValue = (aValue || '').toString().toLowerCase();
          bValue = (bValue || '').toString().toLowerCase();
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [attendanceData, selectedName, selectedStatus, sortColumn, sortDirection, filterDateBounds]);

  // Pagination logic (based on filtered results)
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, itemsPerPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return (
        <svg className="w-4 h-4 text-white opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    if (sortDirection === 'asc') {
      return (
        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      );
    }
    return (
      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  const toggleColumn = (columnKey) => {
    setVisibleColumns(prev => {
      // Count currently visible columns
      const visibleCount = Object.values(prev).filter(v => v).length;
      
      // If trying to uncheck a column and it's the last visible one, prevent it
      if (prev[columnKey] && visibleCount === 1) {
        // Show a brief message or prevent action
        // You can add a toast notification here if needed
        return prev; // Don't change anything
      }
      
      // Otherwise, allow the toggle
      return {
        ...prev,
        [columnKey]: !prev[columnKey]
      };
    });
  };
  const handleResetColumns = () => {
    setVisibleColumns(defaultVisibleColumns);
  };

  const columnDefinitions = [
    { key: 'employeeId', label: 'Emp ID' },
    { key: 'date', label: 'Date' },
    { key: 'name', label: 'Name' },
    { key: 'startTime', label: 'Start Time' },
    { key: 'startLocation', label: 'Start Location' },
    { key: 'startImage', label: 'Start Image' },
    { key: 'endTime', label: 'End Time' },
    { key: 'endLocation', label: 'End Location' },
    { key: 'endImage', label: 'End Image' },
    { key: 'status', label: 'Status' },
    { key: 'advance', label: 'Advance' },
    { key: 'role', label: 'Role' },
    { key: 'actions', label: 'Actions' },
    { key: 'image', label: 'Image' },
    { key: 'phone', label: 'Phone' },
    { key: 'email', label: 'Email' },
    { key: 'address', label: 'Address' },
    { key: 'checkIn', label: 'In Time' },
    { key: 'checkOut', label: 'Out Time' },
    { key: 'location', label: 'Location' }
  ];

  const getVisibleColumnCount = () => {
    return Object.values(visibleColumns).filter(v => v).length;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    
    try {
      const rawDate = formData.attendanceDate;
      const normalizedDate = toAttendanceDateYmd(rawDate);
      const trimmedRaw = rawDate == null ? '' : String(rawDate).trim();
      let attendanceDateYmd = normalizedDate;
      if (!attendanceDateYmd) {
        if (!trimmedRaw) {
          attendanceDateYmd = getTodayDate();
        } else {
          setError('Attendance date must be YYYY-MM-DD only (e.g. 2026-04-08).');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
          return;
        }
      }
      if (!ATTENDANCE_DATE_YMD.test(attendanceDateYmd)) {
        setError('Attendance date must be YYYY-MM-DD only (e.g. 2026-04-08).');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }

      // Convert time from HH:MM or HH:MM:SS string to API format { hour, minute, second, nano }
      const parseTimeToObject = (timeStr) => {
        if (!timeStr) return { hour: 0, minute: 0, second: 0, nano: 0 };
        const parts = (timeStr || '').split(':');
        return {
          hour: parseInt(parts[0], 10) || 0,
          minute: parseInt(parts[1], 10) || 0,
          second: parseInt(parts[2], 10) || 0,
          nano: 0
        };
      };

      // Build request body matching updated API schema
      const recordData = {
        id: isEditMode ? (formData.recordId ?? 0) : 0,
        empId: formData.employeeId || '',
        firstName: formData.firstName || '',
        lastName: formData.lastName || '',
        role: formData.role || '',
        attendanceDate: attendanceDateYmd,
        timeIn: parseTimeToObject(formData.startTime),
        timeOut: parseTimeToObject(formData.endTime),
        location: formData.startLocation || '',
        endLocation: formData.endLocation || '',
        status: formData.status || 'PRESENT',
        photoBase64: formData.photoBase64 || '',
        photoTimestamp: formData.photoTimestamp || new Date().toISOString(),
        advance: formData.advance ? parseFloat(formData.advance) : 0,
        employeeId: formData.employeeId || '',
        comments: formData.comments || ''
      };

      if (isEditMode) {
        await updateAttendanceRecord(formData.recordId, recordData);
        console.log('Attendance record updated successfully');
        setSuccessMessage('Attendance record updated successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } else {
        // Create new record
        await createAttendanceRecord(recordData);
        console.log('Attendance record created successfully');
        setSuccessMessage('Attendance record added successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      }
      
      // Clear any previous errors
      setError('');
      
      // Refresh the data
      await fetchAttendanceData();
      
      // Close modal and reset form
      setIsModalOpen(false);
      setIsEditMode(false);
      setFormData({
        employeeId: '',
        firstName: '',
        lastName: '',
        empName: '',
        role: '',
        phone: '',
        email: '',
        attendanceDate: getTodayDate(),
        startTime: '',
        startLocation: '',
        endTime: '',
        endLocation: '',
        status: 'PRESENT',
        advance: '',
        comments: ''
      });
      
    } catch (error) {
      console.error('Error saving attendance record:', error);
      setError(error.message || 'Failed to save attendance record');
      setSuccessMessage(''); // Clear success message on error
      setSnackbarOpen(false);
    }
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setFormData({
      employeeId: '',
      firstName: '',
      lastName: '',
      empName: '',
      role: '',
      phone: '',
      email: '',
      attendanceDate: getTodayDate(),
      startTime: '',
      startLocation: '',
      endTime: '',
      endLocation: '',
      status: 'PRESENT',
      advance: '',
      comments: ''
    });
    setError('');
    setSuccessMessage('');
    setSnackbarOpen(false);
  };

  const handleEdit = (record) => {
    // Pre-fill the form with existing data from originalData or record
    const originalData = record.originalData || record;
    
    const attendanceDate =
      toAttendanceDateYmd(originalData.attendanceDate)
      || toAttendanceDateYmd(originalData.date)
      || toAttendanceDateYmd(record?.date)
      || getTodayDate();
    
    // Parse time from time object or string format to HH:MM for input
    const parseTime = (timeData) => {
      if (!timeData) return '';
      
      // If it's an object with hour, minute properties
      if (typeof timeData === 'object' && timeData.hour !== undefined) {
        return `${String(timeData.hour || 0).padStart(2, '0')}:${String(timeData.minute || 0).padStart(2, '0')}`;
      }
      
      // If it's a string in 12h format (e.g., "09:00 AM")
      if (typeof timeData === 'string') {
        if (timeData === '-') return '';
        if (timeData.includes('AM') || timeData.includes('PM')) {
          const [time, period] = timeData.split(' ');
          const [hours, minutes] = time.split(':');
          let hour24 = parseInt(hours, 10);
          if (period === 'PM' && hour24 !== 12) hour24 += 12;
          if (period === 'AM' && hour24 === 12) hour24 = 0;
          return `${String(hour24).padStart(2, '0')}:${minutes}`;
        }
        // If already in 24h format, remove seconds if present (HH:MM:SS -> HH:MM)
        if (timeData.includes(':')) {
          const parts = timeData.split(':');
          return `${parts[0]}:${parts[1]}`;
        }
      }
      
      return '';
    };
    
    // Split name into first and last
    const nameParts = (record.name || '').split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    const locationForForm = (val) => {
      const raw = val ?? '';
      return raw && raw !== '-' ? String(raw) : '';
    };
    
    setFormData({
      recordId: originalData.id,
      employeeId: record.employeeId,
      firstName: originalData.firstName || firstName,
      lastName: originalData.lastName || lastName,
      empName: originalData.empName || record.name || '',
      role: originalData.role || record.role || '',
      phone: originalData.phone || record.phoneNumber || '',
      email: originalData.email || record.email || '',
      attendanceDate,
      startTime: parseTime(originalData.timeIn) || parseTime(record.timeIn) || '',
      startLocation: locationForForm(originalData.location ?? record.location),
      endTime: parseTime(originalData.timeOut) || parseTime(record.timeOut) || '',
      endLocation: locationForForm(originalData.endLocation ?? record.endLocation),
      status: originalData.status || (record.status === 'Present' ? 'PRESENT' : record.status === 'Absent' ? 'ABSENT' : record.status) || 'PRESENT',
      advance: originalData.advance?.toString() || record.advance || '',
      comments: originalData.comments ?? ''
    });
    setIsEditMode(true);
    setIsModalOpen(true);
  };

  const handleDelete = (recordId) => {
    setRecordToDelete(recordId);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!recordToDelete) return;
    
    try {
      await deleteAttendanceRecord(recordToDelete);
      console.log('Attendance record deleted successfully');
      setSuccessMessage('Attendance record deleted successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      // Refresh the data
      await fetchAttendanceData();
      // Close confirmation dialog
      setShowDeleteConfirm(false);
      setRecordToDelete(null);
    } catch (error) {
      console.error('Error deleting attendance record:', error);
      setError(error.message || 'Failed to delete attendance record');
      setSuccessMessage('Failed to delete attendance record');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      setShowDeleteConfirm(false);
      setRecordToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setRecordToDelete(null);
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    
    // Add title
    doc.setFontSize(16);
    doc.text('Attendance Report', 14, 15);
    
    // Add date range if available
    if (dateDisplay) {
      doc.setFontSize(10);
      doc.text(`Date Range: ${dateDisplay}`, 14, 22);
    }
    
    // Prepare table data
    const tableData = filteredData.map(item => [
      item.employeeId ?? '',
      formatDateMMMDDYYYY(item.date === '-' ? '' : item.date),
      item.name,
      item.timeIn,
      item.timeOut,
      item.advance,
      item.status
    ]);
    
    // Add table
    autoTable(doc, {
      startY: dateDisplay ? 28 : 22,
      head: [['Employee ID', 'Date', 'Name', 'Start Time', 'End Time', 'Advance', 'Status']],
      body: tableData,
      styles: { fontSize: 7 },
      headStyles: { fillColor: [0, 150, 136], textColor: 255 },
      margin: { top: 20 }
    });
    
    // Save the PDF
    doc.save(`Attendance_Report_${new Date().toISOString().slice(0,10)}.pdf`);
  };

  const handleExportExcel = () => {
    const headers = ['Employee ID', 'Date', 'Name', 'Start Time', 'End Time', 'Advance', 'Status'];
    const rows = filteredData.map(item => [
      item.employeeId ?? '',
      formatDateMMMDDYYYY(item.date === '-' ? '' : item.date),
      item.name, 
      item.timeIn, 
      item.timeOut, 
      item.advance, 
      item.status
    ]);
    
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Attendance Report');
    
    // Set column widths
    const columnWidths = [
      { wch: 8 },  // ID
      { wch: 12 }, // Date
      { wch: 12 }, // Name
      { wch: 10 }, // Start Time
      { wch: 10 }, // End Time
      { wch: 10 }, // Advance
      { wch: 10 }  // Status
    ];
    worksheet['!cols'] = columnWidths;
    
    XLSX.writeFile(workbook, `Attendance_Report_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const handleGenerateReport = () => {
    // Export filtered rows to CSV
    const headers = ['Employee ID', 'Date', 'Name', 'Start Time', 'End Time', 'Advance', 'Status'];
    const rows = filteredData.map(item => [
      item.employeeId ?? '',
      formatDateMMMDDYYYY(item.date === '-' ? '' : item.date),
      item.name, 
      item.timeIn, 
      item.timeOut, 
      item.advance, 
      item.status
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => (v == null ? '' : String(v).replace(/"/g,'""'))).map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Attendance_Report_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    
  };

  const handleCloseSnackbar = (event, reason) => {
    // if (reason === 'clickaway') {
    //   return;
    // }
    setSnackbarOpen(false);
    setSuccessMessage('');
  };

  return (
    <>
     {/* Success Snackbar */}
     <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        style={{position: 'absolute', bottom: '10px', left: '10px'}}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbarSeverity}
          sx={{ 
            width: '100%', 
            backgroundColor: snackbarSeverity === 'success' ? '#43a047' : '#dc2626', 
            color: '#fff', 
            fontWeight: '400',
            fontSize: '18px' 
          }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

       {/* Error Message */}
       {error && (
        <div className="bg-red-200 border border-red-400 text-red-700 px-4 py-3 rounded" style={{position: 'absolute', bottom: '10px', left: '10px', zIndex: '1000', minWidth: '200px'}}>
          {error}
        </div>
      )}
      
    <div className="space-y-6">
     
     

     
      {/* {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-6 flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
          <span className="text-gray-700 font-medium">Loading user details data...</span>
        </div>
      </div>
      )} */}

      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-poppins">Attendance</h1>
         
        </div>
        <div className="flex items-center space-x-3">
          <button
            type="button"
            onClick={fetchAttendanceData}
            className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            title="Refresh"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v6h6M20 20v-6h-6M20 8a8 8 0 00-14.828-3M4 16a8 8 0 0014.828 3" />
            </svg>
            
          </button>
          {/* Column Visibility Toggle */}
          <div className="relative column-menu-container">
            <button 
              onClick={() => setShowColumnMenu(!showColumnMenu)}
              className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              title="Show/Hide Columns"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            
            </button>
            {showColumnMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-96 overflow-y-auto">
                <div className="p-2">
                  <div className="px-3 py-2 text-xs font-semibold text-gray-700 uppercase border-b border-gray-200">
                    Toggle Columns
                  </div>
                  <div className="px-3 py-2 border-b border-gray-200">
                    <button
                      type="button"
                      onClick={handleResetColumns}
                      className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Reset
                    </button>
                  </div>
                  {columnDefinitions.map((col) => {
                    const isLastVisible = visibleColumns[col.key] && getVisibleColumnCount() === 1;
                    return (
                      <label
                        key={col.key}
                        className={`flex items-center px-3 py-2 hover:bg-gray-100 ${
                          isLastVisible ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                        }`}
                        title={isLastVisible ? 'At least one column must remain visible' : ''}
                      >
                        <input
                          type="checkbox"
                          checked={visibleColumns[col.key]}
                          onChange={() => toggleColumn(col.key)}
                          disabled={isLastVisible}
                          className="mr-2 h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded disabled:cursor-not-allowed"
                        />
                        <span className={`text-sm ${isLastVisible ? 'text-gray-500' : 'text-gray-700'}`}>{col.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          {isAdministrator && (
            <>
             
              <button 
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                title={showFilters ? "Hide Filters" : "Show Filters"}
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
                </svg>
                <span>{showFilters ? "Hide Filters" : "Show Filters"}</span>
              </button>

              {/* <button 
                onClick={() => {
                  setIsEditMode(false);
                  setFormData({
                    employeeId: '',
                    firstName: '',
                    lastName: '',
                    empName: '',
                    role: '',
                    phone: '',
                    email: '',
                    attendanceDate: getTodayDate(),
                    timeIn: '',
                    timeOut: '',
                    status: 'PRESENT',
                    advance: ''
                  });
                  setIsModalOpen(true);
                }}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                style={{ backgroundColor: '#009688' }}
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>Add Attendance</span>
              </button> */}
            </>
          )}
        </div>
      </div>
      {isAdministrator && showFilters && (
            <div className="p-4 flex flex-wrap items-end gap-4 bg-white shadow rounded-lg" style={{marginTop: '10px'}}>
              <div className="flex flex-col">
                <label className="text-xs text-gray-600 mb-1">
                  {datePreset == '' ? 'Choose Date' : 'Selected Date Range: '} 
                {datePreset && <span className="font-bold text-black text-xs ">({datePreset == "today" ? "Today" : datePreset == "yesterday" ? "Yesterday" : datePreset == "last7" ? "Last 7 Days" : datePreset == "last30" ? "Last 30 Days" : datePreset == "thisMonth" ? "This Month" : datePreset == "lastMonth" ? "Last Month" : datePreset == "custom" ? "Custom Range" : datePreset})</span>}
                </label>
                <div className="relative date-dropdown-container">
                  <div className="relative">
                    <input
                      type="text"
                      value={dateDisplay || 'Select Range'}
                      readOnly
                      onClick={() => setShowDateDropdown(!showDateDropdown)}
                      className="w-full px-3 py-2 pl-10 pr-8 border border-gray-300 rounded-md text-sm bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Select Date Range"
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    {dateDisplay && (
                      <button
                        type="button"
                        onClick={handleClearDate}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center hover:bg-gray-100 rounded-r-md"
                        title="Clear date"
                      >
                        <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                  {showDateDropdown && (
                    <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg z-20">
                      <div className="py-1">
                        <button
                          onClick={() => handleDatePresetChange('today')}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'today' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                        >
                          Today
                        </button>
                        <button
                          onClick={() => handleDatePresetChange('yesterday')}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'yesterday' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                        >
                          Yesterday
                        </button>
                        <button
                          onClick={() => handleDatePresetChange('last7')}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'last7' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                        >
                          Last 7 Days
                        </button>
                        <button
                          onClick={() => handleDatePresetChange('last30')}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'last30' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                        >
                          Last 30 Days
                        </button>
                        <button
                          onClick={() => handleDatePresetChange('thisMonth')}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'thisMonth' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                        >
                          This Month
                        </button>
                        <button
                          onClick={() => handleDatePresetChange('lastMonth')}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'lastMonth' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                        >
                          Last Month
                        </button>
                        <button
                          onClick={() => handleDatePresetChange('custom')}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'custom' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                        >
                          Custom Range
                        </button>
                      </div>
                    </div>
                  )}
                  {/* Custom Calendar Modal */}
                  {showCustomCalendar && (
                    <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-30 p-4">
                      <div className="flex">
                        {/* Left side - Preset options */}
                        <div className="w-48 pr-4 border-r border-gray-200">
                          <div className="space-y-1">
                            <button
                              onClick={() => handleDatePresetChange('today')}
                              className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'today' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                            >
                              Today
                            </button>
                            <button
                              onClick={() => handleDatePresetChange('yesterday')}
                              className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'yesterday' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                            >
                              Yesterday
                            </button>
                            <button
                              onClick={() => handleDatePresetChange('last7')}
                              className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'last7' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                            >
                              Last 7 Days
                            </button>
                            <button
                              onClick={() => handleDatePresetChange('last30')}
                              className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'last30' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                            >
                              Last 30 Days
                            </button>
                            <button
                              onClick={() => handleDatePresetChange('thisMonth')}
                              className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'thisMonth' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                            >
                              This Month
                            </button>
                            <button
                              onClick={() => handleDatePresetChange('lastMonth')}
                              className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'lastMonth' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                            >
                              Last Month
                            </button>
                            <button
                              onClick={() => handleDatePresetChange('custom')}
                              className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'custom' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                            >
                              Custom Range
                            </button>
                          </div>
                        </div>
                        
                        {/* Right side - Calendar */}
                        <div className="ml-4">
                          {/* Calendar Navigation */}
                          <div className="flex items-center justify-between mb-4">
                            <button
                              onClick={() => navigateMonth(-1)}
                              className="p-1 hover:bg-gray-100 rounded"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                              </svg>
                            </button>
                            <h3 className="text-sm font-medium text-gray-900">
                              {currentMonth.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                            </h3>
                            <button
                              onClick={() => navigateMonth(1)}
                              className="p-1 hover:bg-gray-100 rounded"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                          </div>
                          
                          {/* Calendar Grid */}
                          <div className="w-64">
                            {/* Days of week header */}
                            <div className="grid grid-cols-7 gap-1 mb-2">
                              {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                                <div key={day} className="text-xs text-gray-500 text-center py-1">
                                  {day}
                                </div>
                              ))}
                            </div>
                            
                            {/* Calendar days */}
                            <div className="grid grid-cols-7 gap-1">
                              {(() => {
                                const daysInMonth = getDaysInMonth(currentMonth);
                                const firstDay = getFirstDayOfMonth(currentMonth);
                                const days = [];
                                
                                // Previous month days
                                const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 0);
                                for (let i = firstDay - 1; i >= 0; i--) {
                                  const day = prevMonth.getDate() - i;
                                  days.push(
                                    <button
                                      key={`prev-${day}`}
                                      className="text-xs text-gray-400 py-1 hover:bg-gray-100 rounded"
                                      disabled
                                    >
                                      {day}
                                    </button>
                                  );
                                }
                                
                                // Current month days
                                for (let day = 1; day <= daysInMonth; day++) {
                                  const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
                                  const isSelected = isSameDate(date, customStartDate) || isSameDate(date, customEndDate);
                                  const isInRange = customStartDate && customEndDate && isDateInRange(date, customStartDate, customEndDate);
                                  
                                  days.push(
                                    <button
                                      key={day}
                                      onClick={() => handleDateClick(date)}
                                      className={`text-xs py-1 rounded hover:bg-gray-100 ${
                                        isSelected 
                                          ? 'bg-orange-500 text-white' 
                                          : isInRange 
                                          ? 'bg-orange-100 text-orange-600' 
                                          : 'text-gray-700'
                                      }`}
                                    >
                                      {day}
                                    </button>
                                  );
                                }
                                
                                // Next month days
                                const remainingCells = 42 - days.length; // 6 rows * 7 days
                                for (let day = 1; day <= remainingCells; day++) {
                                  days.push(
                                    <button
                                      key={`next-${day}`}
                                      className="text-xs text-gray-400 py-1 hover:bg-gray-100 rounded"
                                      disabled
                                    >
                                      {day}
                                    </button>
                                  );
                                }
                                
                                return days;
                              })()}
                            </div>
                          </div>
                          
                          {/* Selected date range display */}
                          <div className="mt-4 text-center">
                            <div className="text-sm text-gray-600 mb-3">
                              {customStartDate && customEndDate 
                                ? `${formatDate(customStartDate)} - ${formatDate(customEndDate)}`
                                : customStartDate 
                                ? `${formatDate(customStartDate)} - Select end date`
                                : 'Select start date'
                              }
                            </div>
                            
                            {/* Action buttons */}
                            <div className="flex gap-2 justify-center">
                              <button
                                onClick={handleCancelCustomRange}
                                className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={handleApplyCustomRange}
                                disabled={!customStartDate || !customEndDate}
                                className="px-4 py-2 text-sm bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                              >
                                Apply
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col min-w-[180px]">
                <label className="text-xs text-gray-600 mb-1">Name</label>
                <div className="relative name-autocomplete-container">
                  <input
                    type="text"
                    value={nameInputValue}
                    onChange={handleNameInputChange}
                    onFocus={() => {
                      setShowNameDropdown(true);
                    }}
                    placeholder="Search "
                    className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    autoComplete="off"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center">
                    {nameInputValue ? (
                      <button
                        type="button"
                        onClick={handleClearName}
                        className="pr-3 hover:bg-gray-100 rounded-r-md flex items-center"
                        title="Clear name"
                      >
                        <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    ) : (
                      <div className="pr-3 pointer-events-none">
                        <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  {showNameDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                      <div className="py-1">
                        {filteredNameOptions.length > 0 ? (
                          filteredNameOptions.map(opt => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => handleNameSelect(opt)}
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 focus:bg-orange-100 focus:outline-none ${
                                selectedName === opt.value ? 'bg-orange-100 text-orange-600 font-medium' : 'text-gray-700'
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-sm text-gray-500 text-center">
                            No matches found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col min-w-[180px]">
                <label className="text-xs text-gray-600 mb-1">Status</label>
                <div className="relative status-autocomplete-container">
                  <input
                    type="text"
                    value={statusInputValue}
                    onChange={handleStatusInputChange}
                    onFocus={() => {
                      setShowStatusDropdown(true);
                    }}
                    placeholder="Select "
                    className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    autoComplete="off"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center">
                    {statusInputValue ? (
                      <button
                        type="button"
                        onClick={handleClearStatus}
                        className="pr-3 hover:bg-gray-100 rounded-r-md flex items-center"
                        title="Clear status"
                      >
                        <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    ) : (
                      <div className="pr-3 pointer-events-none">
                        <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    )}
                  </div>
                  {showStatusDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                      <div className="py-1">
                        {filteredStatusOptions.length > 0 ? (
                          filteredStatusOptions.map(opt => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => handleStatusSelect(opt)}
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 focus:bg-orange-100 focus:outline-none ${
                                selectedStatus === opt.value ? 'bg-orange-100 text-orange-600 font-medium' : 'text-gray-700'
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-sm text-gray-500 text-center">
                            No matches found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <button 
                  onClick={handleClearFilters}
                  disabled={!hasActiveFilters}
                  className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                  title="Clear Filters"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span>Clear All</span>
                </button>
                <button 
                  onClick={handleExportPDF}
                  className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  title="Export as PDF"
                >
                  <img src="/assets/img/pdf.png" alt="PDF" className="h-4 w-4" />
                 
                </button>
                <button 
                  onClick={handleExportExcel}
                  className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  title="Export as Excel"
                >
                  <img src="/assets/img/sheet.png" alt="Excel" className="h-4 w-4" />
                  {/* <span> Excel</span> */}
                </button>
              </div>
            </div>
          )}

{isLoading ? (
        <div className="bg-white shadow rounded-lg p-8 flex items-center justify-center min-h-[400px]">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
            <p className="text-gray-600 text-sm">Loading data...</p>
          </div>
        </div>
      ) : (
        <Grid cols={1} className="p-0 flex flex-wrap items-end rounded-lg" style={{marginTop: '10px'}}>
          <div className="">
            {/* Filters */}
          
            <div className="overflow-x-auto">
            <div 
                className={`relative overflow-y-auto ${!showFilters ? 'bg-white' : ''}`}
                style={isAdministrator ? { height: 'calc(100vh - 370px)', backgroundColor: '#fff' } :  { height: 'calc(100vh - 270px)', backgroundColor: '#fff' }
                }
              >
                <table className="min-w-full divide-y divide-gray-200 shadow rounded-lg border border-gray-200">
                <thead className="sticky top-0 z-10" style={{backgroundColor: "rgb(0, 150, 136)"}}>
                  <tr>
                    {visibleColumns.employeeId && (
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{backgroundColor: "rgb(0, 150, 136)"}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                        onClick={() => handleSort('employeeId')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Emp ID</span>
                          {getSortIcon('employeeId')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.date && (
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{backgroundColor: "rgb(0, 150, 136)"}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                        onClick={() => handleSort('date')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Date</span>
                          {getSortIcon('date')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.name && (
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{backgroundColor: "rgb(0, 150, 136)"}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                        onClick={() => handleSort('name')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Name</span>
                          {getSortIcon('name')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.startTime && (
                      <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Start Time</th>
                    )}
                    {visibleColumns.startLocation && (
                      <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Start Location</th>
                    )}
                    {visibleColumns.startImage && (
                      <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Start Image</th>
                    )}
                    {visibleColumns.endTime && (
                      <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>End Time</th>
                    )}
                    {visibleColumns.endLocation && (
                      <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>End Location</th>
                    )}
                    {visibleColumns.endImage && (
                      <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>End Image</th>
                    )}
                    {visibleColumns.status && (
                      <th
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{backgroundColor: "rgb(0, 150, 136)"}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                        onClick={() => handleSort('status')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Status</span>
                          {getSortIcon('status')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.advance && (
                      <th
                        className="px-6 py-3 text-right text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{backgroundColor: "rgb(0, 150, 136)"}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                        onClick={() => handleSort('advance')}
                      >
                        <div className="flex items-center justify-end space-x-1">
                          <span>Advance</span>
                          {getSortIcon('advance')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.role && (
                      <th
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{backgroundColor: "rgb(0, 150, 136)"}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                        onClick={() => handleSort('role')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Role</span>
                          {getSortIcon('role')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.actions && (
                      <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Actions</th>
                    )}
                    {visibleColumns.image && (
                      <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Image</th>
                    )}
                    {visibleColumns.phone && (
                      <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Phone</th>
                    )}
                    {visibleColumns.email && (
                      <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Email</th>
                    )}
                    {visibleColumns.address && (
                      <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Address</th>
                    )}
                    {visibleColumns.checkIn && (
                      <th
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{backgroundColor: "rgb(0, 150, 136)"}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                        onClick={() => handleSort('checkIn')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>In Time</span>
                          {getSortIcon('checkIn')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.checkOut && (
                      <th
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{backgroundColor: "rgb(0, 150, 136)"}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                        onClick={() => handleSort('checkOut')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Out Time</span>
                          {getSortIcon('checkOut')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.location && (
                      <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Location</th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                {!isLoading && paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan={getVisibleColumnCount()} className="px-6 py-8 text-center text-sm text-gray-500">
                      No attendance records found.
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((record) => (
                    <tr key={record.recordId || record.employeeId}>
                      {visibleColumns.employeeId && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{record.employeeId}</td>
                      )}
                      {visibleColumns.date && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatDateMMMDDYYYY(record.date === '-' ? '' : record.date)}
                        </td>
                      )}
                      {visibleColumns.name && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          <button
                            onClick={() => handleEmployeeClick(record)}
                            className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                          >
                            {record.name}
                          </button>
                        </td>
                      )}
                      {visibleColumns.startTime && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{record.timeIn}</td>
                      )}
                      {visibleColumns.startLocation && (
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                          <div className="truncate" title={record.location || '-'}>
                            {record.location || '-'}
                          </div>
                        </td>
                      )}
                      {visibleColumns.startImage && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <button
                            type="button"
                            onClick={() => handleOpenImagePreview(record.startImage)}
                            disabled={!record.startImage}
                            className={`p-1.5 rounded-md border ${
                              record.startImage
                                ? 'text-blue-600 border-blue-200 hover:bg-blue-50'
                                : 'text-gray-400 border-gray-200 cursor-not-allowed'
                            }`}
                            title={record.startImage ? 'View start image' : 'No start image'}
                          >
                            <VisibilityIcon className="w-4 h-4" />
                          </button>
                        </td>
                      )}
                      {visibleColumns.endTime && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{record.timeOut}</td>
                      )}
                      {visibleColumns.endLocation && (
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                          <div className="truncate" title={record.endLocation || '-'}>
                            {record.endLocation || '-'}
                          </div>
                        </td>
                      )}
                      {visibleColumns.endImage && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <button
                            type="button"
                            onClick={() => handleOpenImagePreview(record.endImage)}
                            disabled={!record.endImage}
                            className={`p-1.5 rounded-md border ${
                              record.endImage
                                ? 'text-blue-600 border-blue-200 hover:bg-blue-50'
                                : 'text-gray-400 border-gray-200 cursor-not-allowed'
                            }`}
                            title={record.endImage ? 'View end image' : 'No end image'}
                          >
                            <VisibilityIcon className="w-4 h-4" />
                          </button>
                        </td>
                      )}
                      {visibleColumns.status && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            record.status === 'Present' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {record.status}
                          </span>
                        </td>
                      )}
                      {visibleColumns.advance && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right">{record.advance}</td>
                      )}
                      {visibleColumns.role && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{record.role}</td>
                      )}
                      {visibleColumns.actions && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEdit(record)}
                              className="text-indigo-600 hover:text-indigo-900 focus:outline-none focus:underline"
                              title="Edit"
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDelete(record.recordId)}
                              className="text-red-600 hover:text-red-900 focus:outline-none focus:underline"
                              title="Delete"
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      )}
                      {visibleColumns.image && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <button
                            type="button"
                            onClick={() => handleOpenImagePreview(record.employeeImage)}
                            disabled={!record.employeeImage}
                            className={`p-1.5 rounded-md border ${
                              record.employeeImage
                                ? 'text-blue-600 border-blue-200 hover:bg-blue-50'
                                : 'text-gray-400 border-gray-200 cursor-not-allowed'
                            }`}
                            title={record.employeeImage ? 'View image' : 'No image'}
                          >
                            <VisibilityIcon className="w-4 h-4" />
                          </button>
                        </td>
                      )}
                      {visibleColumns.phone && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{record.phoneNumber}</td>
                      )}
                      {visibleColumns.email && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{record.email}</td>
                      )}
                      {visibleColumns.address && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{record.address}</td>
                      )}
                      {visibleColumns.checkIn && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{record.timeIn}</td>
                      )}
                      {visibleColumns.checkOut && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{record.timeOut}</td>
                      )}
                      {visibleColumns.location && (
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                          <div className="truncate" title={record.location || '-'}>
                            {record.location || '-'}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
              </div>
            <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        itemsPerPage={itemsPerPage}
        totalItems={filteredData.length}
        onItemsPerPageChange={handleItemsPerPageChange}
      />
          </div>
        </div>
      </Grid>
      )}

      {/* Image Preview Modal */}
      {isImagePreviewOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            onClick={handleCloseImagePreview}
          />
          <div
            className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">Employee Image</h3>
              <button
                type="button"
                onClick={handleCloseImagePreview}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 flex justify-center">
              <img
                src={previewImageSrc}
                alt="Employee"
                className="max-h-[70vh] w-auto rounded-md border border-gray-200"
              />
            </div>
          </div>
        </div>
      )}
    

      {/* Add Employee Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50" style={{marginTop: 0}}>
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={handleClose}
            ></div>

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all modal-card">
              <form onSubmit={handleSubmit}>
                {/* Header */}
                <div className="bg-white px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-900">
                    {isEditMode ? 'Edit Attendance Record' : 'Add Attendance Record'}
                  </h3>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Form content */}
                <div className="bg-white px-6 py-4">
                  <div className="grid grid-cols-3 gap-6 mb-4">
                    {/* Employee ID */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Emp ID <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="employeeId"
                        value={formData.employeeId}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>

                    {/* Attendance Date */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Attendance Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        name="attendanceDate"
                        value={formData.attendanceDate}
                        onChange={handleInputChange}
                        max={getTodayDate()}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                  
                    </div>

                    {/* Start Time */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Time
                      </label>
                      <input
                        type="time"
                        name="startTime"
                        value={formData.startTime}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>

                    {/* Start Location */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Location
                      </label>
                      <input
                        type="text"
                        name="startLocation"
                        value={formData.startLocation || ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="Enter start location"
                      />
                    </div>

                    {/* End Time */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        End Time
                      </label>
                      <input
                        type="time"
                        name="endTime"
                        value={formData.endTime}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>

                    {/* End Location */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        End Location
                      </label>
                      <input
                        type="text"
                        name="endLocation"
                        value={formData.endLocation || ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="Enter end location"
                      />
                    </div>

                    {/* Status */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Status <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        <option value="PRESENT">Present</option>
                        <option value="ABSENT">Absent</option>
                        <option value="LATE">Late</option>
                        <option value="HALF_DAY">Half Day</option>
                      </select>
                    </div>

                    {/* Advance */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Advance
                      </label>
                      <input
                        type="number"
                        name="advance"
                        value={formData.advance}
                        onChange={handleInputChange}
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="bg-gray-50 px-6 py-3 flex justify-end space-x-3 modal-card-bottom">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    Close
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    style={{ backgroundColor: '#009688' }}
                  >
                    {isEditMode ? 'Update' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Background overlay */}
          <div 
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            onClick={handleCancelDelete}
          ></div>

          {/* Confirmation Dialog */}
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 transform transition-all">
            <div className="p-6">
              {/* Warning Icon */}
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
                <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              
              {/* Message */}
              <h3 className="text-lg font-medium text-gray-900 text-center mb-2">
                Confirm Delete
              </h3>
              <p className="text-sm text-gray-500 text-center mb-6">
                Are you sure you want to delete this attendance record? This action cannot be undone.
              </p>
              
              {/* Action buttons */}
              <div className="flex gap-3 justify-center">
                <button
                  type="button"
                  onClick={handleCancelDelete}
                  className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDelete}
                  className="px-6 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 hover:opacity-90"
                  style={{ backgroundColor: '#dc2626' }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Employee Details Modal */}
      {isEmployeeModalOpen && selectedEmployee && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Background overlay */}
          <div 
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            onClick={handleCloseEmployeeModal}
          ></div>

          {/* Modal content */}
          <div 
            className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto transform transition-all"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with close button */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
              <h2 className="text-xl font-semibold text-gray-900">Employee Details</h2>
              <button
                onClick={handleCloseEmployeeModal}
                className="text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="flex flex-col lg:flex-row gap-6">
                {/* Left Panel - Employee Profile and Basic Information */}
                <div className="w-full lg:w-6/6 space-y-6">
                  {/* Employee Profile Card */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    {/* Green Header Banner */}
                    <div className="bg-primary px-6 py-4">
                      <div className="flex items-center space-x-4">
                        {/* Employee Image */}
                        <div className="w-16 h-16 bg-gray-300 rounded-full flex items-center justify-center">
                          {selectedEmployee.employeeImage ? (
                            <img
                              src={selectedEmployee.employeeImage}
                              alt={selectedEmployee.name}
                              className="w-16 h-16 rounded-full object-cover"
                            />
                          ) : (
                            <PersonIcon className="w-8 h-8 text-gray-500" />
                          )}
                        </div>
                        
                        <div className="flex-1">
                          <h1 className="text-2xl font-bold text-white">{selectedEmployee.name}</h1>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className="bg-white text-primary px-3 py-1 rounded-full text-sm font-medium">
                              {selectedEmployee.role}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Employee Details */}
                    <div className="p-6">
                      <div className="space-y-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          <span className="text-gray-600">Employee ID:</span>
                          <span className="font-medium">{selectedEmployee.employeeId}</span>
                        </div>
                        
                        <div className="flex items-center space-x-3">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          <span className="text-gray-600">Role:</span>
                          <span className="font-medium">{selectedEmployee.role}</span>
                        </div>
                        
                        <div className="flex items-center space-x-3">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          <span className="text-gray-600">Date Of Join:</span>
                          <span className="font-medium">{selectedEmployee.dateOfJoin || '-'}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Basic Information Card */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic information</h3>
                    
                    <div className="space-y-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <span className="text-gray-600">Phone:</span>
                        <span className="font-medium">{selectedEmployee.phoneNumber || '-'}</span>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <span className="text-gray-600">Alternate No:</span>
                        <span className="font-medium">{selectedEmployee.alternatePhone || '-'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Edit Profile Modal */}
          {isEditModalOpen && (
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]"
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  handleCloseEditModal();
                }
              }}
            >
              <div 
                className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">Edit Profile</h2>
                    <button
                      onClick={handleCloseEditModal}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <form className="space-y-6">
                    {/* Profile Image */}
                    <div className="flex items-center space-x-4">
                      <div className="w-20 h-20 bg-gray-300 rounded-full flex items-center justify-center overflow-hidden">
                        {editFormData.employeeImage ? (
                          <img
                            src={editFormData.employeeImage}
                            alt="Profile"
                            className="w-20 h-20 rounded-full object-cover"
                          />
                        ) : (
                          <PersonIcon className="w-10 h-10 text-gray-500" />
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Profile Image
                        </label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                      </div>
                    </div>

                    {/* Basic Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={editFormData.name}
                          onChange={handleEditInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Employee ID
                        </label>
                        <input
                          type="text"
                          value={selectedEmployee.id || selectedEmployee.employeeId}
                          disabled
                          className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Phone Number *
                        </label>
                        <input
                          type="tel"
                          name="phoneNumber"
                          value={editFormData.phoneNumber}
                          onChange={handleEditInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Alternate Phone
                        </label>
                        <input
                          type="tel"
                          name="alternatePhone"
                          value={editFormData.alternatePhone}
                          onChange={handleEditInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={editFormData.email}
                          onChange={handleEditInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Role *
                        </label>
                        <select
                          name="role"
                          value={editFormData.role}
                          onChange={handleEditInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        >
                          <option value="">Select Role</option>
                          <option value="Supervisor">Supervisor</option>
                          <option value="Manager">Manager</option>
                          <option value="Employee">Employee</option>
                          <option value="Admin">Admin</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Site
                        </label>
                        <input
                          type="text"
                          name="site"
                          value={editFormData.site}
                          onChange={handleEditInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Date of Join
                        </label>
                        <input
                          type="date"
                          name="dateOfJoin"
                          value={editFormData.dateOfJoin}
                          onChange={handleEditInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Address *
                      </label>
                      <textarea
                        name="address"
                        value={editFormData.address}
                        onChange={handleEditInputChange}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={handleCloseEditModal}
                        className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveProfile}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        Save Changes
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
    </>
  );
};

export default Attendance;

